import { PriorityTypes } from '../../../enums/PriorityEnum';
import { AutomationStatusEnum } from '../../../enums/enumRegistry';
import { TestGroupStatus } from '../../../enums/TestGroupEnums';
import { StatusTypes } from '../../../enums/StatusEnum';
import { UserStatusTypes } from '../../../enums/UserEnums';
import { AIModelStatus } from '../../../enums/AIModelEnums';
import { ExecutionResultStatus, TestLaunchStatus } from '../../../enums/TestLaunchEnums';
import { FEEDBACK_TYPES, FEEDBACK_STATUSES } from '../../../enums/FeedbackEnumFrontend';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PauseCircleIcon,
} from "@heroicons/react/24/outline";

// Semantic color variant constants following industry best practices
const COLOR_VARIANTS = {
  ERROR: 'red',      // Failed, Critical, High Priority, Urgent states
  SUCCESS: 'green',  // Passed, Completed, Active, Automated states
  INFO: 'blue',      // Information, Normal Priority, In Progress states
  WARNING: 'yellow', // Pending, Skipped, Caution, Attention states
  SPECIAL: 'purple', // NLP, Premium, Advanced, Unique features
  NEUTRAL: 'gray',   // Low Priority, Manual, Aborted, Disabled, Inactive states
  DEFAULT: 'default' // Fallback for unknown states
};


// Generic semantic variants - Direct color mapping
export const getSemanticVariant = (meaning) => {
  if (!meaning) return COLOR_VARIANTS.DEFAULT;
  
  switch (meaning) {
    case 'success':
      return COLOR_VARIANTS.SUCCESS;
    case 'error':
      return COLOR_VARIANTS.ERROR;
    case 'warning':
      return COLOR_VARIANTS.WARNING;
    case 'info':
      return COLOR_VARIANTS.INFO;
    case 'special':
      return COLOR_VARIANTS.SPECIAL;
    case 'neutral':
      return COLOR_VARIANTS.NEUTRAL;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Priority mapping - Based on urgency level
export const getPriorityVariant = (priority) => {
  if (!priority) return COLOR_VARIANTS.DEFAULT;
  
  switch (priority) {
    case PriorityTypes.HIGH:
      return COLOR_VARIANTS.ERROR;     
    case PriorityTypes.NORMAL:
      return COLOR_VARIANTS.INFO;
    case PriorityTypes.LOW:
      return COLOR_VARIANTS.NEUTRAL;    
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Automation status mapping - Based on completion/capability level
export const getAutomationVariant = (automationStatus) => {
  switch (automationStatus) {
    case AutomationStatusEnum.NOT_AUTOMATED:
      return COLOR_VARIANTS.NEUTRAL;
    case AutomationStatusEnum.IN_PROGRESS:
      return COLOR_VARIANTS.WARNING;
    case AutomationStatusEnum.NLP_AUTOMATED:
      return COLOR_VARIANTS.SPECIAL;
    case AutomationStatusEnum.EXTERNALLY_AUTOMATED:
      return COLOR_VARIANTS.SUCCESS;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Test group status mapping - Based on execution result
export const getTestGroupStatusVariant = (status) => {
  switch (status) {
    case TestGroupStatus.PASSED:
      return COLOR_VARIANTS.SUCCESS;
    case TestGroupStatus.FAILED:
      return COLOR_VARIANTS.ERROR;
    case TestGroupStatus.PENDING:
      return COLOR_VARIANTS.WARNING;
    case TestGroupStatus.IN_PROGRESS:
      return COLOR_VARIANTS.INFO;
    case TestGroupStatus.ABORTED:
      return COLOR_VARIANTS.NEUTRAL;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Feedback status variant mapping
export const getFeedbackStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;

  switch (status) {
    case FEEDBACK_STATUSES.NEW:
      return COLOR_VARIANTS.INFO;
    case FEEDBACK_STATUSES.REVIEWED:
      return COLOR_VARIANTS.WARNING;
    case FEEDBACK_STATUSES.ACTIONED:
      return COLOR_VARIANTS.SUCCESS;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Feedback type variant mapping
export const getFeedbackTypeVariant = (type) => {
  if (!type) return COLOR_VARIANTS.DEFAULT;

  switch (type) {
    case FEEDBACK_TYPES.BUG_REPORT:
      return COLOR_VARIANTS.ERROR;
    case FEEDBACK_TYPES.CONFUSING_FLOW:
      return COLOR_VARIANTS.WARNING;
    case FEEDBACK_TYPES.FEATURE_REQUEST:
      return COLOR_VARIANTS.INFO;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Test Case Type variant mapping - Returns default variant for all types
export const getTestCaseTypeVariant = (type) => {
  if (!type) return COLOR_VARIANTS.DEFAULT;
  
  // All test case types use the same default variant
  return COLOR_VARIANTS.DEFAULT;
};

// Test Case Status variant mapping
export const getTestCaseStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case StatusTypes.NEW:
      return COLOR_VARIANTS.INFO;
    case StatusTypes.INREVIEW:
      return COLOR_VARIANTS.WARNING;
    case StatusTypes.ACTIVE:
      return COLOR_VARIANTS.SUCCESS;
    case StatusTypes.REJECTED:
      return COLOR_VARIANTS.ERROR;
    case StatusTypes.RETIRED:
      return COLOR_VARIANTS.NEUTRAL;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Automation Status variant mapping
export const getAutomationStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case AutomationStatusEnum.NOT_AUTOMATED:
      return COLOR_VARIANTS.NEUTRAL;
    case AutomationStatusEnum.IN_PROGRESS:
      return COLOR_VARIANTS.WARNING;
    case AutomationStatusEnum.NLP_AUTOMATED:
      return COLOR_VARIANTS.SPECIAL;
    case AutomationStatusEnum.EXTERNALLY_AUTOMATED:
      return COLOR_VARIANTS.SUCCESS;
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// User Status variant mapping
export const getUserStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case UserStatusTypes.ACTIVE:
      return COLOR_VARIANTS.SUCCESS;    
    case UserStatusTypes.INACTIVE:
      return COLOR_VARIANTS.WARNING;   
    case UserStatusTypes.DISABLED:
      return COLOR_VARIANTS.ERROR;     
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// AI Model Status variant mapping
export const getAIModelStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case AIModelStatus.ACTIVE:
      return COLOR_VARIANTS.SUCCESS;    
    case AIModelStatus.INACTIVE:
      return COLOR_VARIANTS.WARNING;   
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Role Type variant mapping
export const getRoleTypeVariant = (roleType) => {
  if (!roleType) return COLOR_VARIANTS.DEFAULT;
  
  switch (roleType) {
    case 'Default':
      return COLOR_VARIANTS.INFO;       
    case 'Organization':
      return COLOR_VARIANTS.SUCCESS;    
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// User Role variant mapping
export const getUserRoleVariant = (role) => {
  if (!role) return COLOR_VARIANTS.DEFAULT;
  
  // All user roles use INFO variant (Blue)
  return COLOR_VARIANTS.INFO;
};

// Group Type variant mapping
export const getGroupTypeVariant = (groupType) => {
  if (!groupType) return COLOR_VARIANTS.DEFAULT;
  
  switch (groupType) {
    case 'Static':
      return COLOR_VARIANTS.INFO;      
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Execution Result Status variant mapping
export const getExecutionResultStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case ExecutionResultStatus.PASSED:
      return COLOR_VARIANTS.SUCCESS;    
    case ExecutionResultStatus.FAILED:
      return COLOR_VARIANTS.ERROR;     
    case ExecutionResultStatus.IN_PROGRESS:
      return COLOR_VARIANTS.INFO;      
    case ExecutionResultStatus.PENDING:
      return COLOR_VARIANTS.WARNING;    
    case ExecutionResultStatus.SKIPPED:
      return COLOR_VARIANTS.WARNING;   
    case ExecutionResultStatus.ABORTED:
      return COLOR_VARIANTS.NEUTRAL;    
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Test Launch Status variant mapping
export const getTestLaunchStatusVariant = (status) => {
  if (!status) return COLOR_VARIANTS.DEFAULT;
  
  switch (status) {
    case TestLaunchStatus.COMPLETED:
      return COLOR_VARIANTS.SUCCESS;    // Green - Successfully completed
    case TestLaunchStatus.ABORTED:
      return COLOR_VARIANTS.ERROR;      // Red - Aborted/Stopped
    case TestLaunchStatus.IN_PROGRESS:
      return COLOR_VARIANTS.INFO;       // Blue - Currently running
    case TestLaunchStatus.NEW:
      return COLOR_VARIANTS.NEUTRAL;    // Gray - Not started
    case TestLaunchStatus.PAUSED:
      return COLOR_VARIANTS.WARNING;    // Yellow - Paused/On hold
    default:
      return COLOR_VARIANTS.DEFAULT;
  }
};

// Generic status icon mapping - works for execution results and other status types
export const getStatusIcon = (status) => {
  if (!status) return null;
  
  switch (status) {
    // Execution Result Status
    case ExecutionResultStatus.PASSED:
      return <CheckCircleIcon />;
    case ExecutionResultStatus.FAILED:
      return <XCircleIcon />;
    case ExecutionResultStatus.IN_PROGRESS:
      return <ClockIcon />;
    case ExecutionResultStatus.PENDING:
      return <ClockIcon />;
    case ExecutionResultStatus.SKIPPED:
      return <ExclamationTriangleIcon />;
    case ExecutionResultStatus.ABORTED:
      return <ExclamationTriangleIcon />;
    
    // Test Launch Status
    case TestLaunchStatus.COMPLETED:
      return <CheckCircleIcon />;
    case TestLaunchStatus.ABORTED:
      return <XCircleIcon />;
    case TestLaunchStatus.IN_PROGRESS:
      return <ClockIcon />;
    case TestLaunchStatus.NEW:
      return <ClockIcon />;
    case TestLaunchStatus.PAUSED:
      return <PauseCircleIcon />;
    
    default:
      return null;
  }
};
